import React from 'react';
import { Box, Chip, SxProps, Theme, useTheme } from '@mui/material';
import { Comment as CommentIcon } from '@mui/icons-material';

export type CategoryLabelStyle = 'short' | 'display' | 'uppercase';

const CATEGORY_LABELS: Record<string, Record<CategoryLabelStyle, string>> = {
  main: { short: 'main', display: 'Main PDF', uppercase: 'MAIN' },
  attachment: { short: 'attachment', display: 'Attachment', uppercase: 'ATTACHMENT' },
  archive: { short: 'internal/archive', display: 'Internal Archive', uppercase: 'ARCHIVE' },
  native: { short: 'native', display: 'Native File', uppercase: 'NATIVE' },
  crs: { short: 'crs', display: 'CRS', uppercase: 'CRS' },
};

export function isCrsCategory(category: string | undefined | null): boolean {
  return (category || '').toLowerCase() === 'crs';
}

export function getCategoryLabel(
  category: string,
  style: CategoryLabelStyle = 'display',
  prefix?: string,
): string {
  const normalized = (category || '').toLowerCase();
  let base = CATEGORY_LABELS[normalized]?.[style];
  if (!base) {
    base = style === 'uppercase' ? normalized.toUpperCase() : normalized;
  }
  if (style === 'short' && normalized === 'main') {
    base = 'official pdf';
  }
  if (prefix) {
    return `${prefix} ${base}`;
  }
  return base;
}

export interface FileCategoryChipProps {
  category: string;
  labelStyle?: CategoryLabelStyle;
  label?: string;
  prefix?: string;
  size?: 'small';
  variant?: 'outlined' | 'filled';
  sx?: SxProps<Theme>;
}

export const CrsCategoryIcon: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const theme = useTheme();
  const size = compact ? 20 : 24;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: theme.palette.error.main,
        flexShrink: 0,
      }}
    >
      <CommentIcon
        sx={{
          color: theme.palette.error.contrastText,
          fontSize: compact ? '0.75rem' : '0.875rem',
        }}
      />
    </Box>
  );
};

export const FileCategoryChip: React.FC<FileCategoryChipProps> = ({
  category,
  labelStyle = 'display',
  label,
  prefix,
  size = 'small',
  variant,
  sx,
}) => {
  const theme = useTheme();
  const displayLabel = label ?? getCategoryLabel(category, labelStyle, prefix);
  const isCrs = isCrsCategory(category);

  if (isCrs) {
    return (
      <Chip
        label={displayLabel}
        size={size}
        sx={{
          height: 20,
          fontSize: '0.65rem',
          fontWeight: 600,
          letterSpacing: '0.03em',
          textTransform: labelStyle === 'short' ? 'uppercase' : undefined,
          borderRadius: 999,
          bgcolor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          ...sx,
        }}
      />
    );
  }

  return (
    <Chip
      label={displayLabel}
      size={size}
      variant={variant ?? 'outlined'}
      sx={{
        height: 20,
        fontSize: '0.65rem',
        letterSpacing: labelStyle === 'display' ? '0.05em' : '0.03em',
        textTransform: labelStyle === 'short' || labelStyle === 'uppercase' ? 'uppercase' : undefined,
        ...sx,
      }}
    />
  );
};
